import React, { ChangeEvent, useEffect, useState } from "react";
import classes from "./DCInput.module.scss";
import { ReactSVG } from "react-svg";

interface DCInputProps {
    value: string;
    maxLength: number;
    placeholder: string;
    required?: boolean;
    disable: boolean;
    onInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
    inputMode?: "text"|"numeric";
    pattern?: string;
    validationMessage?: string;
    onKeyUp?:(event:React.KeyboardEvent<HTMLInputElement>)=> void;  
    onClickSave?:boolean   
    onValidityChange?: (isValid: boolean) => void;
    onBlur?: () => void;
    onFocus?: () => void;
    isWarning?: boolean;
}

export const DCInput = ({
    value,
    maxLength,
    placeholder,
    required = true,
    disable,
    onInputChange,
    inputMode = 'text',
    pattern,
    validationMessage,
    onKeyUp,
    onClickSave,
    onValidityChange,
    onBlur,
    onFocus,
    isWarning = false
}: DCInputProps) => {

    //States
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [inputValue, setInputValue] = useState<string>(value);
    const [isValid, setIsValid] = useState<boolean>(true);
    const [touch, setTouch] = useState<boolean>(false);

    //Effects
    useEffect(() => {        
        if (inputMode === 'numeric' && /\D/.test(inputValue)) {
            //Get previous value
            const previousValue = inputValue;
            //Remove non-digit characters
            setInputValue(previousValue.replace(/\D/g, ''));            
            return;
        }
        onInputChange({ currentTarget: { value: inputValue }, target: { value: inputValue } } as ChangeEvent<HTMLInputElement>);        
        if (required && inputValue.length === 0 && (touch || onClickSave)) {
            setIsValid(false);
        } else {
            setIsValid(true);                        
        }
        if(pattern && onClickSave) {
            const regex = new RegExp(pattern);
            if(!regex.test(inputValue)) {
                setIsValid(false);
            }
        }    
    }, [inputValue, required,onClickSave]);

    useEffect(() => {
        setInputValue(value);
    }, [value]);

    useEffect(() => {
        if (pattern && onClickSave) {
            const regex = new RegExp(pattern);
            if (!regex.test(inputValue)) {
                setIsValid(false);
            }
        }
    }, [onClickSave]);

    useEffect(() => {
        if (onValidityChange) {
            onValidityChange(isValid);
        }
    }, [isValid, onValidityChange]);
    
    //Event Handlers
    const handleClearInput = () => {
        setInputValue("");
        setTouch(true);
        onInputChange({ currentTarget: { value: "" }, target: { value: "" } } as ChangeEvent<HTMLInputElement>);
        if(required) {
            setIsValid(false);
        }
    }
    //Handle Blur
    const handleBlur = () => {
        if (required && inputValue.length === 0) {
            setIsValid(false);
        } else {
            setIsValid(true);
        }
        //validate pattern if any
        if(pattern) {
            const regex = new RegExp(pattern);
            if(!regex.test(inputValue)) {
                setIsValid(false);
            }
        }
        setTouch(true);
        setIsFocused(false);
        // Call parent onBlur if provided
        if (onBlur) {
            onBlur();
        }
    }

    //Handle KeyUp 
    const handleKeyUp=(e:React.KeyboardEvent<HTMLInputElement>)=>{
        setTouch(true);
        if(onKeyUp){
            onKeyUp(e)
        } 
        if(pattern) {
            const regex = new RegExp(pattern);
            if(!regex.test(inputValue)) {
                setIsValid(false);
            }
        }   
    }

    const effectiveIsWarning = !!isWarning && isValid;
    let inputClassName = `${classes.DCInputInput} ${classes.DCInputInputNotValid} ${disable ? classes.DCInputInputDisabled : ''}`;

    if (effectiveIsWarning) {
        inputClassName = `${classes.DCInputInput} ${classes.DCInputInputWarning} ${disable ? classes.DCInputInputDisabled : ''}`;
    } else if (isValid) {
        inputClassName = `${classes.DCInputInput} ${classes.DCInputInputValid} ${disable ? classes.DCInputInputDisabled : ''}`;
    }

    const shouldShowWarningIcon = effectiveIsWarning;
    const shouldShowErrorIcon = !effectiveIsWarning && !isValid;
    const shouldShowValidationMessage = !effectiveIsWarning && !isValid;
    let statusIcon = null;

    if (shouldShowWarningIcon) {
        statusIcon = (
            <div className={classes.DCInputInputWarningIcon}>
                <ReactSVG src="https://cdn.jsdelivr.net/npm/@alaskaairux/icons@latest/dist/icons/alert/error.svg" />
            </div>
        );
    } else if (shouldShowErrorIcon) {
        statusIcon = (
            <div className={classes.DCInputInputErrorIcon}>
                <ReactSVG src="https://cdn.jsdelivr.net/npm/@alaskaairux/icons@latest/dist/icons/alert/error.svg" />
            </div>
        );
    }

    return (
        <div>
            <div className={inputClassName}>                             
                {statusIcon}
                <input
                    data-testid="dcInputInput"
                    inputMode={inputMode}                    
                    maxLength={maxLength}
                    value={inputValue}
                    disabled={disable}
                    pattern={pattern}
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => { setInputValue(e.target.value); }}
                    onBlur={handleBlur}
                    onFocus={() => {
                        setIsFocused(true);
                        if (onFocus) {
                            onFocus();
                        }
                    }}
                    onKeyUp={handleKeyUp}
                />            
           
            <span data-testid="dcInputLabel" slot="label">{placeholder}</span>
            </div>   
            {shouldShowValidationMessage ? (
                <div className={classes.DCInputInputErrorMessage}>
                    <span>{validationMessage || 'Please fill out this field.'}</span>
                </div>
            ) : null}
        </div>
    );
};